import React, { useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ETIQUETA_MAX } from '../types/dispositivos.types';

interface Props {
  etiqueta: string | null;
  /** «Equipo 7F3A»: para los nombres accesibles de los botones. */
  nombre: string;
  /** Lanza con el mensaje para mostrar si no se pudo guardar. */
  onGuardar: (etiqueta: string | null) => Promise<void>;
}

/**
 * La etiqueta del equipo, editable en línea («Handheld 2 · caja»). Es lo único
 * que distingue dos handhelds idénticas además del código: lo ideal es que
 * coincida con la etiqueta física pegada en el equipo (§12).
 *
 * Enter guarda y Escape cancela. Vacía se guarda como `null` (sin etiqueta).
 * El borrador vive aquí: el refresco de la lista cada 30 s no lo pisa.
 */
export const EtiquetaEditable: React.FC<Props> = ({ etiqueta, nombre, onGuardar }) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(etiqueta ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editando) campo.current?.focus();
  }, [editando]);

  const abrir = () => {
    setBorrador(etiqueta ?? '');
    setError(null);
    setEditando(true);
  };

  const cancelar = () => {
    setEditando(false);
    setError(null);
  };

  const guardar = async () => {
    const limpia = borrador.trim().slice(0, ETIQUETA_MAX);
    const nueva = limpia === '' ? null : limpia;
    if (nueva === (etiqueta ?? null)) {
      setEditando(false);
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(nueva);
      setEditando(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la etiqueta.');
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return (
      <div className="disp-etiqueta">
        <span className={etiqueta ? 'disp-etiqueta-texto' : 'disp-etiqueta-vacia'}>
          {etiqueta || 'Sin etiqueta'}
        </span>
        <Button
          type="button"
          icon="pi pi-pencil"
          className="p-button-text p-button-sm p-button-rounded disp-boton-icono"
          onClick={abrir}
          aria-label={`Editar etiqueta de ${nombre}`}
          tooltip="Editar etiqueta"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  }

  return (
    <div className="disp-etiqueta-editor">
      <InputText
        ref={campo}
        value={borrador}
        maxLength={ETIQUETA_MAX}
        onChange={(e) => setBorrador(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void guardar();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelar();
          }
        }}
        placeholder="Ej.: Handheld 2 · caja"
        aria-label={`Etiqueta de ${nombre}`}
        disabled={guardando}
        className="disp-etiqueta-campo"
      />
      <div className="disp-etiqueta-botones">
        <Button
          type="button"
          label="Guardar"
          icon="pi pi-check"
          className="p-button-sm"
          onClick={() => void guardar()}
          loading={guardando}
        />
        <Button
          type="button"
          label="Cancelar"
          className="p-button-sm p-button-text"
          onClick={cancelar}
          disabled={guardando}
        />
      </div>
      <small className="disp-etiqueta-ayuda">
        {borrador.length}/{ETIQUETA_MAX} · vacía = sin etiqueta
      </small>
      {error && (
        <small className="disp-error-linea" role="alert">
          {error}
        </small>
      )}
    </div>
  );
};
